import { useEffect } from "react";
import type { ReactNode } from "react";
import CompetencesEtPorteesInformationBox from "components/Informations/SousCompetences/CompetencesEtPorteesInformationBox";
import PerimetrePorteesInformationsWithErrorBoundary from "./PerimetrePorteesInformations";
import { useErrorHandler, withComponentErrorBoundary } from "utils/errorHandling";
import type { SdeappsError } from "utils/errorHandling";
import type { Transfert, Perimetre, TransfertCEA } from "models";
import LoadingScreen from "components/Template/LoadingScreen";
import { DisplayDatesProvider } from "providers";
import { MenuItem, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router";
import { routesConfig } from "config/app-config";
import { Authorization } from "@sdeapps/react-core";
import { ApplicationRole } from "constants/ApplicationRole";
import { perimetresUtil } from "utils";
import PerimetreSansTerritoirePorteesInformationsWithErrorBoundary from "./PerimetreSansTerritoirePorteesInformations";

interface PerimetrePorteesInformationsProps {
  transferts: Array<Transfert> | Array<TransfertCEA>;
  perimetre: Perimetre;
  isLoading?: boolean;
  error?: SdeappsError;
}

function PerimetrePorteesInformationsBox({
  transferts,
  perimetre,
  isLoading = false,
  error,
}: Readonly<PerimetrePorteesInformationsProps>): ReactNode {
  const { setOriginalError } = useErrorHandler();
  const navigate = useNavigate();
  useEffect(() => {
    if (error != null) {
      setOriginalError(error);
    }
  }, [error, setOriginalError]);

  const isPerimetreSansTerritoire = perimetresUtil.isPerimetreSansTerritoire(perimetre);

  return (
    <DisplayDatesProvider>
      <CompetencesEtPorteesInformationBox
        noChildrenPadding={false}
        additionalMenuItems={
          isPerimetreSansTerritoire ? (
            <Authorization roles={ApplicationRole.ADMINISTRATOR} key="modification">
              <Tooltip title="Cette fonctionnalité n'est pas disponible pour les Périmètres spéciaux (sans Territoire associé)">
                <span>
                  <MenuItem disabled>
                    <EditIcon color="primary" sx={{ mr: 1 }} />
                    Modification
                  </MenuItem>
                </span>
              </Tooltip>
            </Authorization>
          ) : (
            <Authorization roles={ApplicationRole.ADMINISTRATOR} key="modification">
              <MenuItem
                onClick={() => {
                  navigate(routesConfig.perimetreEditTransferts.getParameterPath(perimetre.id));
                }}>
                <EditIcon color="primary" sx={{ mr: 1 }} />
                Modification
              </MenuItem>
            </Authorization>
          )
        }>
        {isLoading && <LoadingScreen />}
        {!isLoading &&
          (isPerimetreSansTerritoire ? (
            <PerimetreSansTerritoirePorteesInformationsWithErrorBoundary
              transferts={transferts as Array<TransfertCEA>}
              perimetre={perimetre}
            />
          ) : (
            <PerimetrePorteesInformationsWithErrorBoundary
              transferts={transferts as Array<Transfert>}
              perimetre={perimetre}
            />
          ))}
      </CompetencesEtPorteesInformationBox>
    </DisplayDatesProvider>
  );
}

const PerimetrePorteesInformationsBoxWithErrorBoundary = withComponentErrorBoundary(
  PerimetrePorteesInformationsBox
);

export default PerimetrePorteesInformationsBoxWithErrorBoundary;
